/**
 * Presupuesto y gasto de toda la vida (fase 1). Los bloques `budget` / `lifetime` ya vienen
 * dentro de cada campaña de `getmetacampaigns` (con `budget_available`): la pestaña Presupuesto
 * no pide nada más. Puro.
 */
import { metaNum, stripMetaTerrioMark } from './metaFormat';

const text = (v) => (v == null ? '' : String(v).trim());
const LEVELS = ['campaign', 'adset', 'none'];
const SCOPES = ['today', 'lifetime'];

/**
 * Qué es `remaining` (27/10): `today` = lo que queda HOY del presupuesto diario; `lifetime` = lo que
 * queda del presupuesto total; `null` = sin restante. Si la API todavía no manda `remaining_scope`
 * (API sin `remaining_scope`) se deduce igual que la API: con presupuesto total → `lifetime`; si no, con diario → `today`.
 */
export function readMetaRemainingScope(raw, remaining, lifetime, daily) {
  if (raw && Object.prototype.hasOwnProperty.call(raw, 'remaining_scope')) {
    return SCOPES.includes(raw.remaining_scope) ? raw.remaining_scope : null;
  }
  if (remaining == null) return null;
  if (lifetime != null) return 'lifetime';
  if (daily != null) return 'today';
  return null;
}

/** Rótulo del restante según `remaining_scope` (sin scope: el neutro de antes). */
export function metaRemainingLabel(scope) {
  if (scope === 'today') return 'Queda hoy';
  if (scope === 'lifetime') return 'Restante del total';
  return 'Presupuesto restante';
}

export function readMetaAdSetBudget(raw) {
  return {
    id: text(raw?.adset_id),
    name: text(stripMetaTerrioMark(raw?.name)),
    status: text(raw?.effective_status || raw?.status),
    daily: metaNum(raw?.daily),
    lifetime: metaNum(raw?.lifetime),
    remaining: metaNum(raw?.remaining),
    remainingSource: text(raw?.remaining_source) || null,
    remainingScope: readMetaRemainingScope(raw, metaNum(raw?.remaining), metaNum(raw?.lifetime), metaNum(raw?.daily)),
    spentTotal: metaNum(raw?.spent_total),
    bidStrategy: text(raw?.bid_strategy) || null,
    endsAt: text(raw?.end_time) || null,
  };
}

/** `campaign.budget` de la API → modelo. `null` si la API no lo manda. */
export function readMetaBudget(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const level = LEVELS.includes(raw.level) ? raw.level : null;
  return {
    level,
    isCbo: Boolean(raw.is_cbo),
    currency: text(raw.currency).toUpperCase() || null,
    daily: metaNum(raw.daily),
    lifetime: metaNum(raw.lifetime),
    remaining: metaNum(raw.remaining),
    remainingSource: text(raw.remaining_source) || null,
    remainingScope: readMetaRemainingScope(raw, metaNum(raw.remaining), metaNum(raw.lifetime), metaNum(raw.daily)),
    spendCap: metaNum(raw.spend_cap),
    bidStrategy: text(raw.bid_strategy) || null,
    buyingType: text(raw.buying_type) || null,
    spentInPeriod: metaNum(raw.spent_in_period),
    spentTotal: metaNum(raw.spent_total),
    // Inicio real de la campaña (27/10; antes era el inicio de datos de la cuenta). `started_on` de respaldo.
    spentTotalSince: text(raw.spent_total_since) || text(raw.started_on) || null,
    // Barra del total: `budget_used` (cierra con `remaining`); `pct_of_lifetime_used` sale de ahí.
    budgetUsed: metaNum(raw.budget_used),
    budgetUsedSource: text(raw.budget_used_source) || null,
    pctUsed: metaNum(raw.pct_of_lifetime_used),
    endsAt: text(raw.ends_at) || null,
    adsets: (Array.isArray(raw.adsets) ? raw.adsets : []).map(readMetaAdSetBudget).filter((a) => a.id),
  };
}

/** `campaign.lifetime` (gasto y resultados desde que arrancó). */
export function readMetaLifetime(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    currency: text(raw.currency).toUpperCase() || null,
    since: text(raw.since) || null,
    spend: metaNum(raw.spend),
    contacts: metaNum(raw.contacts),
    costPerContact: metaNum(raw.cost_per_contact),
  };
}

const addInto = (acc, key, value) => {
  if (value == null) return;
  acc[key] = (acc[key] || 0) + value;
};

/**
 * Resumen por moneda de las campañas que se muestran (misma regla que `totals_by_currency` de la API,
 * 27/10). Nunca suma monedas distintas ni lo que queda HOY de los diarios con lo que queda de los
 * totales: van en líneas separadas. Cada total es `null` si ninguna campaña aportó ese dato.
 * @param {Array<{ currency?: string|null, budget?: object|null, metrics?: object|null }>} campaigns
 * @returns {Array<{ currency: string, campaignsCount: number, daily: number|null, lifetime: number|null,
 *   dailyCampaignsCount: number, remainingTodaySum: number|null, lifetimeCampaignsCount: number,
 *   remainingLifetimeSum: number|null, lifetimeBudgetSum: number|null, lifetimeUsedSum: number|null,
 *   lifetimePctUsed: number|null, spentInPeriod: number|null, spentTotal: number|null }>}
 */
export function metaBudgetSummaries(campaigns) {
  const byCurrency = new Map();
  (campaigns || []).forEach((c) => {
    const b = c?.budget;
    const currency = text(b?.currency || c?.currency).toUpperCase();
    if (!b || b.level == null || !currency) return;
    const acc = byCurrency.get(currency) || {
      currency, campaignsCount: 0, dailyCampaignsCount: 0, lifetimeCampaignsCount: 0, lifetimeUsedCount: 0,
    };
    acc.campaignsCount += 1;
    addInto(acc, 'daily', b.daily);
    addInto(acc, 'lifetime', b.lifetime);
    if (b.lifetime != null) {
      acc.lifetimeCampaignsCount += 1;
      addInto(acc, 'lifetimeBudgetSum', b.lifetime);
      // API vieja sin `budget_used`: el gasto de toda la vida (lo que antes usaba la barra).
      const used = b.budgetUsed ?? b.spentTotal;
      if (used != null) acc.lifetimeUsedCount += 1;
      addInto(acc, 'lifetimeUsedSum', used);
      if (b.remainingScope === 'lifetime') addInto(acc, 'remainingLifetimeSum', b.remaining);
    } else if (b.daily != null) {
      acc.dailyCampaignsCount += 1;
      if (b.remainingScope === 'today') addInto(acc, 'remainingTodaySum', b.remaining);
    }
    addInto(acc, 'spentInPeriod', b.spentInPeriod ?? metaNum(c.metrics?.spend));
    addInto(acc, 'spentTotal', b.spentTotal);
    byCurrency.set(currency, acc);
  });
  return [...byCurrency.values()].map((a) => {
    const usedComplete = a.lifetimeCampaignsCount > 0 && a.lifetimeUsedCount === a.lifetimeCampaignsCount;
    return {
      currency: a.currency,
      campaignsCount: a.campaignsCount,
      daily: a.daily ?? null,
      lifetime: a.lifetime ?? null,
      dailyCampaignsCount: a.dailyCampaignsCount,
      remainingTodaySum: a.remainingTodaySum ?? null,
      lifetimeCampaignsCount: a.lifetimeCampaignsCount,
      remainingLifetimeSum: a.remainingLifetimeSum ?? null,
      lifetimeBudgetSum: a.lifetimeBudgetSum ?? null,
      lifetimeUsedSum: usedComplete ? a.lifetimeUsedSum ?? null : null,
      lifetimePctUsed: usedComplete && a.lifetimeBudgetSum ? Math.round((1000 * a.lifetimeUsedSum) / a.lifetimeBudgetSum) / 10 : null,
      spentInPeriod: a.spentInPeriod ?? null,
      spentTotal: a.spentTotal ?? null,
    };
  });
}
